import type { ReactNode } from "react";
import { ArrowRight, Check, FileText, Mic, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

export interface LifecycleMockCopy {
  quote: string;
  items: string;
  signed: string;
  job: string;
  hours: string;
  material: string;
  photos: string;
  margin: string;
  diary: string;
  check: string;
  invoice: string;
  posted: string;
  account: string;
  convertFrom: string;
  convertTo: string;
  convertAction: string;
}

export interface LifecycleMockStory {
  title: string;
  client: string;
  amount: string;
  jobNumber: string;
  hourLine: string;
  materialLine: string;
  photoLine: string;
  diaryLine: string;
}

interface MockShellProps {
  title: string;
  children: ReactNode;
  className?: string;
  badge?: string;
}

const MockShell = ({ title, children, className, badge }: MockShellProps) => (
  <div
    className={cn(
      "rounded-xl border border-border/50 bg-background overflow-hidden text-left min-w-0 w-full",
      className
    )}
    aria-hidden
  >
    <div className="flex items-center gap-1.5 border-b border-border/40 bg-muted/20 px-3 py-2">
      <span className="h-1.5 w-1.5 rounded-full bg-destructive/40" />
      <span className="h-1.5 w-1.5 rounded-full bg-amber-400/60" />
      <span className="h-1.5 w-1.5 rounded-full bg-success/50" />
      <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground truncate">
        {title}
      </span>
      {badge ? (
        <span className="ml-auto shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
          {badge}
        </span>
      ) : null}
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="min-w-0 rounded-lg bg-muted/30 px-3.5 py-2.5">
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-0.5 text-base font-semibold text-foreground break-words leading-snug">{value}</p>
  </div>
);

export const QuoteMock = ({
  copy,
  story,
}: {
  copy: LifecycleMockCopy;
  story: LifecycleMockStory;
}) => (
  <MockShell title={`${copy.quote}.pdf`}>
    <div className="grid gap-3 sm:grid-cols-3">
      <Stat label={copy.quote} value={story.title} />
      <Stat label={story.client} value={story.amount} />
      <Stat label={copy.items} value={story.materialLine} />
    </div>
  </MockShell>
);

export const JobMock = ({
  copy,
  story,
}: {
  copy: LifecycleMockCopy;
  story: LifecycleMockStory;
}) => (
  <MockShell title={copy.job} badge={copy.signed}>
    <div className="grid gap-3 sm:grid-cols-3">
      <Stat label={copy.job} value={story.jobNumber} />
      <Stat label={story.client} value={story.title} />
      <Stat label={copy.signed} value={story.amount} />
    </div>
  </MockShell>
);

export const WorkMock = ({
  copy,
  story,
}: {
  copy: LifecycleMockCopy;
  story: LifecycleMockStory;
}) => (
  <MockShell title={story.title}>
    <div className="grid gap-3 sm:grid-cols-3 mb-3">
      <Stat label={copy.hours} value={story.hourLine} />
      <Stat label={copy.material} value={story.materialLine} />
      <Stat label={copy.photos} value={story.photoLine} />
    </div>
    <div className="h-2 rounded-full bg-muted overflow-hidden">
      <div className="h-full w-2/3 rounded-full bg-primary" />
    </div>
    <p className="mt-2 text-xs text-muted-foreground">{copy.margin}</p>
  </MockShell>
);

export const DiaryMock = ({
  copy,
  story,
}: {
  copy: LifecycleMockCopy;
  story: LifecycleMockStory;
}) => (
  <MockShell title={copy.diary}>
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="flex items-start gap-2 rounded-lg bg-muted/40 px-3 py-2 min-w-0">
        <Mic className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-foreground leading-relaxed">{story.diaryLine}</p>
      </div>
      <div className="flex items-center gap-2 rounded-lg bg-primary/5 px-3 py-2">
        <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
        <span className="text-sm font-medium text-foreground">{copy.check}</span>
        <Check className="ml-auto h-4 w-4 text-success shrink-0" />
      </div>
    </div>
  </MockShell>
);

export const InvoiceMock = ({
  copy,
  story,
}: {
  copy: LifecycleMockCopy;
  story: LifecycleMockStory;
}) => (
  <MockShell title={copy.invoice}>
    <div className="grid gap-3 sm:grid-cols-3">
      <Stat label={copy.invoice} value={story.title} />
      <Stat label={story.client} value={story.amount} />
      <div className="min-w-0 rounded-lg bg-muted/40 px-3 py-2 flex items-center gap-2">
        <FileText className="h-4 w-4 text-primary shrink-0" />
        <p className="text-sm font-semibold text-foreground">Peppol · QR</p>
      </div>
    </div>
  </MockShell>
);

export const LedgerMock = ({
  copy,
  story,
}: {
  copy: LifecycleMockCopy;
  story: LifecycleMockStory;
}) => (
  <MockShell title={copy.posted} badge={copy.posted}>
    <div className="grid gap-3 sm:grid-cols-3">
      <Stat label={copy.account} value="604 / 311" />
      <Stat label={story.client} value={story.amount} />
      <Stat label={copy.posted} value={story.jobNumber} />
    </div>
  </MockShell>
);

export const ConvertMock = ({
  copy,
  story,
}: {
  copy: LifecycleMockCopy;
  story: LifecycleMockStory;
}) => (
  <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr] items-center min-w-0">
    <QuoteMock copy={copy} story={story} />
    <div className="flex sm:flex-col items-center justify-center gap-2 py-1">
      <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground whitespace-normal text-center">
        {copy.convertAction}
        <ArrowRight className="h-3.5 w-3.5 shrink-0" />
      </span>
      <span className="text-[10px] text-muted-foreground hidden sm:block">
        {copy.convertFrom} → {copy.convertTo}
      </span>
    </div>
    <JobMock copy={copy} story={story} />
  </div>
);

export const StepMock = ({
  stepIndex,
  copy,
  story,
}: {
  stepIndex: number;
  copy: LifecycleMockCopy;
  story: LifecycleMockStory;
}) => {
  if (stepIndex === 1) return <ConvertMock copy={copy} story={story} />;
  if (stepIndex === 2) return <WorkMock copy={copy} story={story} />;
  if (stepIndex === 3) return <DiaryMock copy={copy} story={story} />;
  if (stepIndex === 4) return <InvoiceMock copy={copy} story={story} />;
  if (stepIndex === 5) return <LedgerMock copy={copy} story={story} />;
  return <QuoteMock copy={copy} story={story} />;
};
